import {
  type FunctionComponent,
  type ReactNode,
  useEffect,
  useRef,
} from 'react';

import type * as RealtimeAiM from 'client/services/ai/realtime';
import type * as AudioClientServiceM from 'client/services/audio';

import LeftSidebar from 'components/LeftSidebar';
import AudioSetup from 'pages/ai-app/AudioSetup';

import { getAudioClientService, getRealtimeAiClient } from 'services';
import { P_AIAPP_CHECK_IN, useGlobalState } from 'utils';

import './style.scss';

type PropsT = {
  children: ReactNode;

  /** Fallback background color to use while the fallback background image,
   *  and the background video are both being loaded. */
  fallbackColor?: string;

  /** URL of the fallback image to render while the background video is being
   *  loaded. You want to pass in here the first frame of the video, exported
   *  into a stand-alone image. */
  fallbackImageUrl?: string;

  hideMobileToggle?: boolean;

  noAudioSetup?: boolean;
  overlayClassName?: string;

  /** Custom sidebar to render instead of the default AI app LeftSidebar
   *  (e.g. the admin app's CmsMenu). When provided, the content area does
   *  not apply the AI app's expand/collapse push classes, leaving it up to
   *  the caller to reserve space for the sidebar via `overlayClassName`. */
  sidebar?: ReactNode;

  /** URL of the video to play in the background. When omitted, a gradient
   *  background is rendered instead. */
  videoUrl?: string;
};

/**
 * Fills entire viewport with given video background, and renders given content
 * on top of it.
 */
const PageWithVideoBackground: FunctionComponent<PropsT> = ({
  children,
  fallbackColor,
  fallbackImageUrl,
  hideMobileToggle,
  noAudioSetup,
  overlayClassName,
  sidebar,
  videoUrl,
}) => {
  const [userId] = useGlobalState('user.id');
  const [isExpanded] = useGlobalState('ui.leftSidebarExpanded');
  const [, setBlockedAudioDeviceAccess] = useGlobalState('ui.blockedAudioDeviceAccess');
  const [showAudioSetup, setShowAudioSetup] = useGlobalState('ui.audioSetup');
  const [audioOutputBlocked, setAudioOutputBlocked] = useGlobalState(
    'ui.audioOutputBlocked',
  );
  // Cleared by the agent's MICROPHONE_RECOVERED event, not by the tap itself.
  const [microphoneLost] = useGlobalState('ui.microphoneLost');
  const [sessionDisconnected, setSessionDisconnected] = useGlobalState(
    'ui.sessionDisconnected',
  );

  // Held in a ref so the recovery below runs synchronously inside the tap - an
  // await there would drop the user activation that permits re-opening the mic.
  // Gated on `microphoneLost`, which only the realtime agent sets: its chunk is
  // loaded by then, and pages that never run the agent do not pull it in.
  const realtimeModuleRef = useRef<null | typeof RealtimeAiM>(null);
  useEffect(() => {
    if (!microphoneLost) return;
    void getRealtimeAiClient()
      .then((m) => {
        realtimeModuleRef.current = m;
      })
      .catch(() => undefined);
  }, [microphoneLost]);

  // Preloaded so the "Tap to enable sound" handler can call play() synchronously
  // in the tap - an await there would drop the iOS user activation.
  const audioModuleRef = useRef<null | typeof AudioClientServiceM>(null);
  useEffect(() => {
    void getAudioClientService()
      .then((m) => {
        audioModuleRef.current = m;
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!noAudioSetup) {
      void (async () => {
        const audio = await getAudioClientService();

        try {
          if (
            showAudioSetup === undefined && (
              !audio.getSelectedInputDeviceId()
              || !audio.getSelectedOutputDeviceId()
            )
          ) setShowAudioSetup(true);

          // Just to ensure the microphone access permission.
          else await audio.getInputStream();
        } catch (error) {
          if (error instanceof Error && error.name === 'NotAllowedError') {
            setBlockedAudioDeviceAccess(true);
          } else throw error;
        }
      })();
    }
  }, [
    noAudioSetup,
    setBlockedAudioDeviceAccess,
    setShowAudioSetup,
    showAudioSetup,
  ]);

  let contentClass = 'no-sidebar';
  if (userId) {
    if (sidebar) contentClass = 'customSidebar';
    else contentClass = isExpanded ? 'expanded' : 'collapsed';
  }

  return (
    <div styleName="container">
      {videoUrl ? (
        <video
          autoPlay
          controls={false}
          disablePictureInPicture
          disableRemotePlayback

          // BEWARE: A <video>'s <source> child doesn't get picked up on its own
          // when only its `src` changes - the element must be reloaded. Keying
          // on `videoUrl` forces a clean remount (and thus a fresh load())
          // whenever the background video changes.
          key={videoUrl}

          loop

          // TODO: For now we mute the video, to avoid any auto-play restrictions
          // (which are applied by browsers only to media with sound). Making the
          // auto-play work for videos with sounds is possible, but requires a bunch
          // more of efforts to correctly handle related permissions, and attempting
          // the auto-play only once it is permitted by browser (after some user
          // interactions with the page).
          muted

          playsInline
          poster={fallbackImageUrl}
          style={{ background: fallbackColor }}
          styleName="background"
        >
          <source src={videoUrl} />
        </video>
      ) : <div styleName="background gradientBackground" />}
      {userId
        ? sidebar ?? <LeftSidebar hideMobileToggle={hideMobileToggle} />
        : null}
      <div
        className={overlayClassName}
        styleName={`content ${contentClass}`}
      >
        {showAudioSetup ? <AudioSetup /> : children}
      </div>
      {audioOutputBlocked ? (
        <button
          onClick={() => {
            // play() synchronously in the tap to keep the iOS user activation.
            void audioModuleRef.current?.playRealtimeAudio().catch(
              () => undefined,
            );
            setAudioOutputBlocked(false);
          }}
          styleName="audioPill"
          type="button"
        >
          Tap to enable sound
        </button>
      ) : null}
      {microphoneLost ? (
        <button
          onClick={() => {
            void realtimeModuleRef.current?.recoverMicInput().catch(
              () => undefined,
            );
          }}
          styleName="audioPill"
          type="button"
        >
          Microphone paused &mdash; tap to resume
        </button>
      ) : null}
      {sessionDisconnected ? (
        <div styleName="audioNotice">
          <p styleName="audioNoticeText">
            Due to lack of activity, your session has ended.
          </p>
          <button
            onClick={() => {
              setSessionDisconnected(false);
              // A full load rather than a route change: the drop usually happens
              // on the check-in route, where routing to it remounts nothing.
              window.location.assign(P_AIAPP_CHECK_IN);
            }}
            styleName="audioNoticeButton"
            type="button"
          >
            Restart
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default PageWithVideoBackground;
